import React from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import { Field } from "formik";
import { useStyles } from "../../campaignModalStyles";

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const parseDaysOfWeek = (value) => {
  try {
    if (!value || value === "" || value === null) return [];
    let parsed;
    if (typeof value === 'string') {
      parsed = JSON.parse(value);
    } else if (Array.isArray(value)) {
      parsed = value;
    } else {
      return [];
    }
    if (Array.isArray(parsed)) {
      return parsed.map(d => typeof d === 'number' ? d : parseInt(d, 10)).filter(d => !isNaN(d));
    }
    return [];
  } catch {
    try {
      const str = String(value).replace(/[[\]]/g, '');
      return str.split(',').map(n => parseInt(n.trim(), 10)).filter(n => !isNaN(n));
    } catch {
      return [];
    }
  }
};

const RecurrenceTabContent = ({ values, setFieldValue, touched, errors, campaignEditable }) => {
  const classes = useStyles();
  const selectedDays = parseDaysOfWeek(values.recurrenceDaysOfWeek);

  return (
    <Grid xs={12} item>
      <Typography variant="h6" style={{ marginBottom: '16px' }}>
        <span role="img" aria-label="Recurrencia">🔄</span> Configuración Recurrente
      </Typography>

      {/* ✅ ELIMINADO: Switch "Habilitar Recurrencia" - Ya no es necesario porque si está en esta pestaña es porque ya es RECURRENTE */}

      <Grid spacing={2} container style={{ marginTop: '8px' }}>
        {/* Tipo de Recurrencia */}
        <Grid xs={12} md={6} item>
          <FormControl
            variant="outlined"
            fullWidth
            margin="dense"
            className={`${classes.formControl} ${classes.selectField}`}
            error={touched.recurrenceType && Boolean(errors.recurrenceType)}
          >
            <InputLabel id="recurrenceType-selection-label">
              Tipo de Recurrencia
            </InputLabel>
            <Field
              as={Select}
              name="recurrenceType"
              label="Tipo de Recurrencia"
              labelId="recurrenceType-selection-label"
              id="recurrenceType"
              disabled={!campaignEditable}
            >
              <MenuItem value="">Seleccione un tipo</MenuItem>
              <MenuItem value="diaria">Diaria</MenuItem>
              <MenuItem value="semanal">Semanal</MenuItem>
              <MenuItem value="quincenal">Quincenal</MenuItem>
              <MenuItem value="mensual">Mensual</MenuItem>
              <MenuItem value="anual">Anual</MenuItem>
            </Field>
            {/* ✅ CORRECCIÓN: Mostrar error debajo del campo como helperText */}
            {touched.recurrenceType && errors.recurrenceType && (
              <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '3px', display: 'block' }}>
                {errors.recurrenceType}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Intervalo */}
        <Grid xs={12} md={6} item>
          <TextField
            label="Intervalo"
            name="recurrenceInterval"
            type="number"
            value={
              values.recurrenceInterval === undefined ||
              values.recurrenceInterval === null ||
              values.recurrenceInterval === "" ? "" : values.recurrenceInterval
            }
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || val === null || val === undefined) {
                setFieldValue('recurrenceInterval', "");
              } else {
                const numVal = parseInt(val, 10);
                setFieldValue('recurrenceInterval', isNaN(numVal) ? "" : numVal);
              }
            }}
            disabled={!campaignEditable}
            helperText={
              touched.recurrenceInterval && errors.recurrenceInterval
                ? errors.recurrenceInterval
                : "Ejemplo: 1 = cada día, 2 = cada 2 días/semanas/meses"
            }
            inputProps={{ min: 1, step: 1 }}
            error={touched.recurrenceInterval && Boolean(errors.recurrenceInterval)}
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField}
          />
        </Grid>

        {/* Días de la Semana (solo si tipo = "semanal") */}
        {values.recurrenceType === "semanal" && (
          <Grid xs={12} item>
            <FormControl
              variant="outlined"
              fullWidth
              margin="dense"
              className={`${classes.formControl} ${classes.selectField}`}
            >
              <InputLabel id="recurrenceDaysOfWeek-selection-label">
                Días de la Semana
              </InputLabel>
              <Select
                multiple
                value={selectedDays}
                onChange={(e) => {
                  setFieldValue('recurrenceDaysOfWeek', JSON.stringify(e.target.value));
                }}
                disabled={!campaignEditable}
                labelId="recurrenceDaysOfWeek-selection-label"
                label="Días de la Semana"
                error={touched.recurrenceDaysOfWeek && Boolean(errors.recurrenceDaysOfWeek)}
                renderValue={(selected) => selected.map((day) => DAY_NAMES[day]).join(', ')}
              >
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <MenuItem key={day} value={day}>
                    <Checkbox checked={selectedDays.includes(day)} />
                    <ListItemText primary={DAY_NAMES[day]} />
                  </MenuItem>
                ))}
              </Select>
              {touched.recurrenceDaysOfWeek && errors.recurrenceDaysOfWeek && (
                <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '3px' }}>
                  {errors.recurrenceDaysOfWeek}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        {/* Día del Mes (solo si tipo = "mensual") */}
        {values.recurrenceType === "mensual" && (
          <Grid xs={12} md={6} item>
            <TextField
              label="Día del Mes"
              name="recurrenceDayOfMonth"
              type="number"
              value={values.recurrenceDayOfMonth || ''}
              onChange={(e) => setFieldValue('recurrenceDayOfMonth', parseInt(e.target.value) || null)}
              disabled={!campaignEditable}
              helperText="Día del mes (1-31)"
              inputProps={{ min: 1, max: 31 }}
              error={touched.recurrenceDayOfMonth && Boolean(errors.recurrenceDayOfMonth)}
              variant="outlined"
              margin="dense"
              fullWidth
              className={classes.textField}
            />
          </Grid>
        )}

        {/* Fecha Final */}
        <Grid xs={12} md={6} item>
          <TextField
            label="Fecha Final (Opcional)"
            name="recurrenceEndDate"
            type="datetime-local"
            value={values.recurrenceEndDate || ''}
            onChange={(e) => setFieldValue('recurrenceEndDate', e.target.value)}
            disabled={!campaignEditable}
            helperText="Dejar vacío para recurrencia infinita"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField}
          />
        </Grid>

        {/* Máximo de Ejecuciones */}
        <Grid xs={12} md={6} item>
          <TextField
            label="Máximo de Ejecuciones (Opcional)"
            name="maxExecutions"
            type="number"
            value={values.maxExecutions || ''}
            onChange={(e) => setFieldValue('maxExecutions', parseInt(e.target.value) || null)}
            disabled={!campaignEditable}
            helperText="Dejar vacío para ilimitado"
            inputProps={{ min: 1 }}
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RecurrenceTabContent;
